import { Component, OnDestroy, OnInit } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { DataLayerService } from 'core/data-layer-service/data-layer.service';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { ProductCategory, ProductsList } from '../entities/product-entity';
import { ProductValidationService } from '../services/product-validation-service/product-validation.service';
import { ProductsService } from '../services/products-service/products.service';

@Component({
  selector: 'app-categories',
  templateUrl: './categories.component.html',
  styleUrls: ['./categories.component.scss'],
  standalone: false
})
export class CategoriesComponent implements OnInit, OnDestroy {

  categories: ProductCategory[] = [];
  currentCategory: any;
  productsList: ProductsList[] = [];
  isLoading: boolean;
  appStrings: AppLocalization;
  catalogs = this.productsService.catalogs;
  selectedCatalogCode: string;
  catalogName: string;
  categoriesPage = [];
  productsPage = [];
  pageSize = 12;
  currentPage = 0;
  totalRecords: number;
  subs = new Subscription();
  noImage;

  categoryId: number;

  constructor(
    private productsService: ProductsService,
    private route: ActivatedRoute,
    private appInfoService: AppInfoService,
    private router: Router,
    private dataLayerService: DataLayerService,
    private productValidationService: ProductValidationService
  ) {
  }

  ngOnInit() {
    this.isLoading = true;
    this.appStrings = this.appInfoService.appStrings;
    this.noImage = this.productsService.noImage;
    this.selectedCatalogCode = this.route.snapshot.params['catalogCode'];
    this.catalogName = this.catalogs.find(c => c.catalogCode === this.selectedCatalogCode)?.name ?? '';
    this.categoryId = Number(this.route.snapshot.params['categoryId']); // Url category id

    this.productsService.getCategories(this.selectedCatalogCode, (categoriesList: ProductCategory[]) => {
      this.categories = categoriesList;

      if (!this.route.snapshot.params['categoryId']) {
        this.currentCategory = this.buildRootCategory(categoriesList);
        this.totalRecords = categoriesList.length;
        this.categoriesPage = categoriesList.slice(0, this.pageSize);
      } else {
        this.currentCategory = this.findCategory(this.categories, this.categoryId);
        if (this.currentCategory) {
          this.totalRecords = this.currentCategory.subcategories.length;
          this.categoriesPage = this.currentCategory.subcategories.slice(0, this.pageSize);
        }
      }

      this.isLoading = false;
      this.checkHasProduct();
    });

    this.subs.add(this.router.events
      .subscribe(event => {
        if (event instanceof NavigationEnd && this.router.url.indexOf('tools/categories') !== -1) {
          this.loadSubcategory();
        }
      }));
    this.dataLayerService.logEvent('category-pageview', {
      category_ID: this.categoryId,
      page_type: 'category',
    });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  checkHasProduct() {
    if (this.currentCategory && this.currentCategory.hasProduct) {
      this.isLoading = true;
      this.productsService.getProducts(this.selectedCatalogCode, this.categoryId, (resultList: ProductsList[]) => {
        this.productsList = resultList;
        this.totalRecords = this.productsList.length;
        this.productsPage = this.productsList.slice(0, this.pageSize);
        this.currentPage = 0;
        this.isLoading = false;
      });
    }
  }

  loadSubcategory() {
    const newCatalogCode = this.route.snapshot.params['catalogCode'];
    this.categoryId = Number(this.route.snapshot.params['categoryId']);

    if (newCatalogCode !== this.selectedCatalogCode) {
      this.selectedCatalogCode = newCatalogCode;
      this.catalogName = this.catalogs.find(c => c.catalogCode === this.selectedCatalogCode)?.name ?? '';
      this.isLoading = true;
      this.productsService.getCategories(this.selectedCatalogCode, (categoriesList: ProductCategory[]) => {
        this.categories = categoriesList;
        this.currentCategory = this.buildRootCategory(categoriesList);
        this.totalRecords = categoriesList.length;
        this.categoriesPage = categoriesList.slice(0, this.pageSize);
        this.currentPage = 0;
        this.isLoading = false;
        this.checkHasProduct();
      });
      return;
    }

    if (!this.route.snapshot.params['categoryId']) {
      this.currentCategory = this.buildRootCategory(this.categories);
      this.totalRecords = this.categories.length;
      this.categoriesPage = this.categories.slice(0, this.pageSize);
    } else {
      this.currentCategory = this.findCategory(this.categories, this.categoryId);
      if (this.currentCategory) {
        this.totalRecords = this.currentCategory.subcategories.length;
        this.categoriesPage = this.currentCategory.subcategories.slice(0, this.pageSize);
      }
    }
    this.currentPage = 0;
    this.checkHasProduct();
  }

  selectCatalog(catalogCode: string) {
    this.router.navigate(['/tools/categories', catalogCode]);
  }

  openProduct(product: { productNumber: string }) {
    this.productValidationService.validateAndOpen({ catalogCode: this.selectedCatalogCode, productNumber: product.productNumber });
  }

  private buildRootCategory(subcategories: ProductCategory[]) {
    return {
      name: this.catalogName,
      breadcrumb: [],
      hasProduct: false,
      subcategories,
      id: -1,
      isCatalog: true
    };
  }

  /**
   * current category/subcategory search
   * @param arr categories array
   * @param id current category id from route.params
   */
  findCategory(arr: ProductCategory[], id: number) {
    let retVal = null;
    arr.forEach(element => {
      if (element.id === id) {
        retVal = element;
      }
    });
    if (!retVal) {
      arr.forEach(element => {
        if (element.subcategories.length && !retVal) {
          retVal = this.findCategory(element.subcategories, id);
        }
      });
    }
    return retVal;
  }

  loadPage(event: PageEvent) {
    this.currentPage = event.pageIndex;
    if (this.currentCategory && this.currentCategory.hasProduct) {
      if (this.productsList.length > this.pageSize) {
        this.productsPage = this.productsList.slice(event.pageIndex * this.pageSize, event.pageIndex * this.pageSize + 12);
      }
    } else {
      if (this.currentCategory.subcategories.length > this.pageSize) {
        this.categoriesPage =
          this.currentCategory.subcategories.slice(event.pageIndex * this.pageSize, event.pageIndex * this.pageSize + 12);
      }
    }
  }
}
